import { useState, useEffect } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import {
  ShieldAlert, CheckCircle2, Loader2, Download,
  Scale, BookOpen, Gavel, TriangleAlert, CircleCheck, ChevronDown, ChevronUp,
  Plus, Trash2, Pencil, X, Check
} from "lucide-react";
import { useAuth } from "../context/AuthContext";

const BASE = process.env.REACT_APP_BACKEND_URL;

// Neumorphism base: light royal blue surface
const nm = {
  base:   "bg-[#e4ecfb]",
  raised: "bg-[#e4ecfb] shadow-[5px_5px_12px_#bcc8e0,-5px_-5px_12px_#ffffff]",
  inset:  "bg-[#e4ecfb] shadow-[inset_4px_4px_8px_#bcc8e0,inset_-4px_-4px_8px_#ffffff]",
  card:   "bg-[#e4ecfb] shadow-[4px_4px_10px_#bcc8e0,-4px_-4px_10px_#ffffff]",
};

// ── Risk Meter ───────────────────────────────────────────────────────────────

function RiskMeter({ score, level }: { score: number; level: string }) {
  const cfg: Record<string, { color: string; label: string; verdict: string }> = {
    critical: { color: "#dc2626", label: "Critical Risk",  verdict: "Cannot proceed as written" },
    high:     { color: "#ea580c", label: "High Risk",      verdict: "Redesign required" },
    medium:   { color: "#ca8a04", label: "Medium Risk",    verdict: "Mitigation required" },
    low:      { color: "#2563eb", label: "Low Risk",       verdict: "Document & monitor" },
    clear:    { color: "#16a34a", label: "Clear",          verdict: "No significant issues" },
  };
  const c = cfg[level] || cfg.medium;

  return (
    <div className={`${nm.card} rounded-xl p-3`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] font-semibold text-[#374151] uppercase tracking-wider">Risk Score</span>
        <span className="text-2xl font-bold" style={{ color: c.color }}>
          {score}<span className="text-xs font-normal text-[#6b7280]">/100</span>
        </span>
      </div>
      <div className={`${nm.inset} rounded-full h-2 mb-2`}>
        <div
          className="h-full rounded-full transition-all"
          style={{ width: `${score}%`, backgroundColor: c.color }}
        />
      </div>
      <div className="flex justify-between items-center">
        <span className="text-xs font-semibold" style={{ color: c.color }}>{c.label}</span>
        <span className="text-[10px] text-[#6b7280]">{c.verdict}</span>
      </div>
    </div>
  );
}

// ── Issue Card ───────────────────────────────────────────────────────────────

function IssueCard({ issue, idx }: { issue: any; idx: number }) {
  const [open, setOpen] = useState(idx === 0);

  const severityColor: Record<string, string> = {
    critical: "#e07070", high: "#e09060", medium: "#c8b060", low: "#6090c8",
  };
  const color = severityColor[issue.severity] || severityColor.medium;
  const verdict = (issue.conclusion || "").split("—")[0].trim();

  return (
    <div className={`${nm.card} rounded-xl overflow-hidden`}>
      <button
        className="w-full px-3 py-2 flex items-center justify-between gap-2 text-left"
        onClick={() => setOpen(!open)}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-1.5 h-6 rounded-full shrink-0" style={{ backgroundColor: color }} />
          <TriangleAlert className="h-3 w-3 shrink-0" style={{ color }} />
          <span className="text-xs font-semibold text-[#111827] truncate">{issue.issue}</span>
          {issue.triggered_by_rule && (
            <span className={`${nm.inset} rounded-full text-[9px] px-1.5 py-0.5 text-[#374151] shrink-0`}>
              rule
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full" style={{ color, backgroundColor: `${color}18` }}>
            {issue.severity}
          </span>
          {open ? <ChevronUp className="h-3 w-3 text-[#6b7280]" /> : <ChevronDown className="h-3 w-3 text-[#6b7280]" />}
        </div>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-2 border-t border-[#c4d0e8]">
          {/* Rule */}
          <div className={`${nm.inset} rounded-lg p-2 mt-2`}>
            <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1 flex items-center gap-1">
              <BookOpen className="h-2.5 w-2.5" /> Rule
            </p>
            <p className="text-[10px] font-semibold text-[#111827]">{issue.rule_reference}</p>
            {issue.rule_text && (
              <p className="text-[10px] text-[#374151] italic mt-0.5">"{issue.rule_text}"</p>
            )}
          </div>

          {/* Application */}
          <div className={`${nm.inset} rounded-lg p-2`}>
            <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1 flex items-center gap-1">
              <Scale className="h-2.5 w-2.5" /> Application
            </p>
            <p className="text-[10px] text-[#1f2937] leading-relaxed">{issue.application}</p>
          </div>

          {/* Conclusion */}
          <div className="rounded-lg p-2" style={{ backgroundColor: `${color}12` }}>
            <p className="text-[9px] font-semibold uppercase tracking-wider mb-1 flex items-center gap-1" style={{ color }}>
              <Gavel className="h-2.5 w-2.5" /> Conclusion
            </p>
            <p className="text-[10px] font-semibold" style={{ color }}>{issue.conclusion}</p>
          </div>

          {/* Penalty */}
          {issue.penalty_exposure && (
            <div className="rounded-lg p-2 bg-[#fee2e2]">
              <p className="text-[9px] font-semibold text-[#991b1b] uppercase tracking-wider mb-0.5">⚠ Penalty Exposure</p>
              <p className="text-[10px] text-[#991b1b]">{issue.penalty_exposure}</p>
            </div>
          )}

          {/* Remediation */}
          {issue.remediation?.length > 0 && (
            <div>
              <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1">Remediation</p>
              <ol className="space-y-1">
                {issue.remediation.map((step: string, i: number) => (
                  <li key={i} className="flex gap-2 text-[10px] text-[#1f2937]">
                    <span className={`${nm.raised} rounded-full w-4 h-4 flex items-center justify-center text-[9px] font-bold text-[#374151] shrink-0`}>
                      {i + 1}
                    </span>
                    <span className="leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Demo Sample ──────────────────────────────────────────────────────────────

const DEMO_ANALYSIS = {
  risk_score: 94, risk_level: "critical",
  executive_summary: "This requirement creates immediate legal exposure under HIPAA, Florida state law, and internal policy. Transmitting PHI via unencrypted email to Florida members violates multiple regulatory safeguards. This requirement cannot be approved in its current form.",
  applicable_frameworks: ["HIPAA Security Rule", "HIPAA Privacy Rule", "Florida DBPA §501.171", "Internal Policy PN-001"],
  issues: [
    {
      issue: "PHI Transmitted via Unencrypted Email", triggered_by_rule: true,
      rule_reference: "HIPAA §164.312(e)(1) — Transmission Security",
      rule_text: "Implement technical security measures to guard against unauthorized access to ePHI transmitted over an electronic communications network.",
      application: "The requirement specifies email as the delivery channel for PHI (member ID, diagnosis code, claim amount). Standard email is not a secure transmission medium.",
      conclusion: "VIOLATION — Email transmission of PHI without encryption controls violates §164.312(e)(1).",
      severity: "critical",
      penalty_exposure: "HIPAA Tier 4: $50,000 per violation, up to $1,919,173 per calendar year",
      remediation: ["Replace email with secure member portal (HTTPS)", "Implement TLS 1.2+ if email is required", "Strip PHI from email body — include only a secure portal link"]
    },
    {
      issue: "Payment Amount in Email Notification", triggered_by_rule: true,
      rule_reference: "Internal Policy PN-001 — Payment Amount in Notifications",
      rule_text: "Payment amounts must not be included in unsecured notifications under any circumstances.",
      application: "The requirement explicitly includes the claim amount in the email. Internal Policy PN-001 categorically blocks this.",
      conclusion: "VIOLATION — Inclusion of claim amount in email is blocked by PN-001.",
      severity: "high",
      penalty_exposure: "Internal policy violation — potential regulatory exposure under HIPAA §164.501 minimum necessary standard",
      remediation: ["Remove claim amount from email entirely", "Display financial figures in authenticated portal only"]
    },
    {
      issue: "Minimum Necessary PHI Exceeded", triggered_by_rule: false,
      rule_reference: "HIPAA §164.502(b) — Minimum Necessary Standard",
      rule_text: "A covered entity must limit PHI to the minimum necessary to accomplish the intended purpose.",
      application: "Bundling member ID, diagnosis code, and claim amount exceeds minimum necessary for a payment notification.",
      conclusion: "VIOLATION — Combination of PHI elements exceeds minimum necessary under §164.502(b).",
      severity: "high",
      penalty_exposure: "HIPAA Tier 2–3: $1,000–$50,000 per violation",
      remediation: ["Remove diagnosis code from notification", "Limit to claim reference number and approval status only"]
    },
    {
      issue: "Florida Breach Notification Exposure", triggered_by_rule: true,
      rule_reference: "Florida DBPA §501.171 — Data Breach Notification",
      rule_text: "Covered entities must notify affected Florida residents within 30 days of a breach determination.",
      application: "Targeting Florida members with maximum PHI per message maximises breach impact if any message is misdirected.",
      conclusion: "RISK — Current design creates breach notification exposure under Florida DBPA §501.171.",
      severity: "high",
      penalty_exposure: "Florida DBPA: Up to $500,000 per breach incident",
      remediation: ["Minimise PHI per message", "Implement message-level audit logging for Florida member communications"]
    }
  ],
  safe_harbours: [
    "HIPAA §164.312(e)(2)(ii): TLS 1.2+ end-to-end encryption may cure transmission security violation",
    "HIPAA §164.508: Written patient authorisation may permit certain PHI disclosures"
  ],
  overall_recommendation: "This requirement must not be approved as written. Escalate to HIPAA Security Officer and legal counsel. Redirect delivery to secure portal, strip diagnosis code and claim amount, document minimum necessary determinations."
};

function DemoSample() {
  const [show, setShow] = useState(false);
  return (
    <div className={`${nm.card} rounded-xl overflow-hidden`}>
      <button
        onClick={() => setShow(v => !v)}
        className="w-full px-3 py-2 flex items-center justify-between"
      >
        <div>
          <p className="text-xs font-semibold text-[#111827] text-left flex items-center gap-1.5">
            <Scale className="h-3 w-3 text-[#374151]" /> Sample Analysis
          </p>
          <p className="text-[10px] text-[#6b7280] text-left">"Send claim approval to Florida members via email"</p>
        </div>
        <div className={`${nm.raised} rounded-full p-1`}>
          {show ? <ChevronUp className="h-3 w-3 text-[#374151]" /> : <ChevronDown className="h-3 w-3 text-[#374151]" />}
        </div>
      </button>

      {show && (
        <div className="px-3 pb-3 space-y-3 border-t border-[#c4d0e8]">
          <div className="pt-2">
            <RiskMeter score={DEMO_ANALYSIS.risk_score} level={DEMO_ANALYSIS.risk_level} />
          </div>
          <div className="flex flex-wrap gap-1.5">
            {DEMO_ANALYSIS.applicable_frameworks.map(f => (
              <span key={f} className={`${nm.raised} rounded-full text-[9px] px-2 py-0.5 text-[#1f2937]`}>{f}</span>
            ))}
          </div>
          <div className={`${nm.inset} rounded-lg p-2`}>
            <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1">Executive Summary</p>
            <p className="text-[10px] text-[#1f2937] leading-relaxed">{DEMO_ANALYSIS.executive_summary}</p>
          </div>
          <div className="space-y-2">
            {DEMO_ANALYSIS.issues.map((issue, i) => <IssueCard key={i} issue={issue} idx={i} />)}
          </div>
          <div className={`${nm.inset} rounded-lg p-2`}>
            <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <CircleCheck className="h-2.5 w-2.5 text-[#16a34a]" /> Safe Harbours
            </p>
            {DEMO_ANALYSIS.safe_harbours.map((s, i) => (
              <p key={i} className="text-[10px] text-[#1f2937] flex gap-1.5 mt-1">
                <span className="text-[#16a34a] shrink-0">▸</span>{s}
              </p>
            ))}
          </div>
          <div className={`${nm.inset} rounded-lg p-2`}>
            <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1 flex items-center gap-1">
              <Gavel className="h-2.5 w-2.5" /> Recommendation
            </p>
            <p className="text-[10px] text-[#1f2937] leading-relaxed">{DEMO_ANALYSIS.overall_recommendation}</p>
          </div>
          <p className="text-[9px] text-[#6b7280] italic">Sample only — does not constitute legal advice.</p>
        </div>
      )}
    </div>
  );
}

// ── Rule Manager ─────────────────────────────────────────────────────────────

const BLANK_RULE = {
  name: "", description: "", domain: "", market: "", channel: "",
  action: "flag", severity: "medium", reference: "",
  condition: { field: "contains_phi", operator: "true", value: "" },
};

function RuleManager({ authHeader }: { authHeader: Record<string, string> }) {
  const [rules, setRules]         = useState<any[]>([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [editId, setEditId]       = useState<string | null>(null);
  const [form, setForm]           = useState<any>({ ...BLANK_RULE });
  const [saving, setSaving]       = useState(false);
  const [error, setError]         = useState<string | null>(null);

  useEffect(() => { fetchRules(); }, []);

  const fetchRules = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${BASE}/api/tools/rules`, { headers: authHeader });
      setRules(data);
    } catch { setRules([]); }
    finally { setLoading(false); }
  };

  const openCreate = () => { setForm({ ...BLANK_RULE }); setEditId(null); setShowForm(true); setError(null); };
  const openEdit   = (r: any) => {
    setForm({
      name: r.name, description: r.description || "", domain: r.domain || "",
      market: r.market || "", channel: r.channel || "", action: r.action,
      severity: r.severity, reference: r.reference || "",
      condition: typeof r.condition === "string" ? JSON.parse(r.condition) : r.condition,
    });
    setEditId(r.id); setShowForm(true); setError(null);
  };

  const handleSave = async () => {
    if (!form.name.trim()) { setError("Name is required"); return; }
    setSaving(true); setError(null);
    const payload = {
      ...form,
      market:  form.market.trim()  || null,
      channel: form.channel.trim() || null,
      condition: {
        field:    form.condition.field,
        operator: form.condition.operator,
        ...(form.condition.value ? { value: form.condition.value } : {}),
      },
    };
    try {
      if (editId) {
        await axios.patch(`${BASE}/api/tools/rules/${editId}`, payload, { headers: authHeader });
      } else {
        await axios.post(`${BASE}/api/tools/rules`, payload, { headers: authHeader });
      }
      await fetchRules();
      setShowForm(false);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Failed to save rule");
    } finally { setSaving(false); }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this rule?")) return;
    try {
      await axios.delete(`${BASE}/api/tools/rules/${id}`, { headers: authHeader });
      setRules(r => r.filter(x => x.id !== id));
    } catch {}
  };

  const toggleActive = async (r: any) => {
    try {
      await axios.patch(`${BASE}/api/tools/rules/${r.id}`, { active: !r.active }, { headers: authHeader });
      setRules(prev => prev.map(x => x.id === r.id ? { ...x, active: !x.active } : x));
    } catch {}
  };

  const severityColor: Record<string, string> = {
    critical: "#e07070", high: "#e09060", medium: "#c8b060", low: "#6090c8",
  };

  const conditionFields = ["contains_phi", "sensitivity_flags", "feature", "channel", "market"];
  const operatorOptions: Record<string, string[]> = {
    contains_phi:      ["true", "false"],
    sensitivity_flags: ["contains"],
    feature:           ["eq"],
    channel:           ["eq"],
    market:            ["eq"],
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-xs font-semibold text-[#374151] uppercase tracking-wider">
          {rules.length} Rule{rules.length !== 1 ? "s" : ""}
        </p>
        <button
          onClick={openCreate}
          className={`${nm.raised} rounded-lg px-2.5 py-1.5 text-xs font-semibold text-[#1f2937] flex items-center gap-1.5 hover:shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] transition-all`}
        >
          <Plus className="h-3 w-3" /> New Rule
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div className={`${nm.card} rounded-xl p-3 space-y-2`}>
          <p className="text-xs font-bold text-[#111827]">{editId ? "Edit Rule" : "New Rule"}</p>

          <input
            placeholder="Rule name *"
            value={form.name}
            onChange={e => setForm({ ...form, name: e.target.value })}
            className={`w-full ${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
            style={{ background: "#e4ecfb" }}
          />
          <input
            placeholder="Description"
            value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            className={`w-full ${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
            style={{ background: "#e4ecfb" }}
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <input placeholder="Domain (e.g. NAIC)" value={form.domain}
              onChange={e => setForm({ ...form, domain: e.target.value })}
              className={`${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
              style={{ background: "#e4ecfb" }} />
            <input placeholder="Market (e.g. Florida)" value={form.market}
              onChange={e => setForm({ ...form, market: e.target.value })}
              className={`${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
              style={{ background: "#e4ecfb" }} />
            <input placeholder="Channel (e.g. email)" value={form.channel}
              onChange={e => setForm({ ...form, channel: e.target.value })}
              className={`${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
              style={{ background: "#e4ecfb" }} />
          </div>
          <input
            placeholder="Reference (e.g. NAIC Model Act §123)"
            value={form.reference}
            onChange={e => setForm({ ...form, reference: e.target.value })}
            className={`w-full ${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
            style={{ background: "#e4ecfb" }}
          />
          <div className="grid grid-cols-2 gap-2">
            <div>
              <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1">Action</p>
              <select value={form.action} onChange={e => setForm({ ...form, action: e.target.value })}
                className={`w-full ${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
                style={{ background: "#e4ecfb" }}>
                <option value="flag">Flag</option>
                <option value="block">Block</option>
                <option value="warn">Warn</option>
              </select>
            </div>
            <div>
              <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1">Severity</p>
              <select value={form.severity} onChange={e => setForm({ ...form, severity: e.target.value })}
                className={`w-full ${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
                style={{ background: "#e4ecfb" }}>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>

          {/* Condition */}
          <div>
            <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1">Condition</p>
            <div className="grid grid-cols-3 gap-2">
              <select value={form.condition.field}
                onChange={e => setForm({ ...form, condition: { field: e.target.value, operator: operatorOptions[e.target.value]?.[0] || "eq", value: "" } })}
                className={`${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
                style={{ background: "#e4ecfb" }}>
                {conditionFields.map(f => <option key={f} value={f}>{f}</option>)}
              </select>
              <select value={form.condition.operator}
                onChange={e => setForm({ ...form, condition: { ...form.condition, operator: e.target.value } })}
                className={`${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
                style={{ background: "#e4ecfb" }}>
                {(operatorOptions[form.condition.field] || ["eq"]).map(op => <option key={op} value={op}>{op}</option>)}
              </select>
              {(form.condition.operator === "eq" || form.condition.operator === "contains") && (
                <input placeholder="Value" value={form.condition.value || ""}
                  onChange={e => setForm({ ...form, condition: { ...form.condition, value: e.target.value } })}
                  className={`${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none`}
                  style={{ background: "#e4ecfb" }} />
              )}
            </div>
          </div>

          {error && <p className="text-[10px] text-[#dc2626]">{error}</p>}

          <div className="flex gap-2 pt-1">
            <button onClick={handleSave} disabled={saving}
              className={`${nm.raised} rounded-lg px-3 py-1.5 text-xs font-semibold text-[#1f2937] flex items-center gap-1.5 hover:shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] transition-all disabled:opacity-40`}>
              {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Check className="h-3 w-3" />}
              {editId ? "Update" : "Create"}
            </button>
            <button onClick={() => setShowForm(false)}
              className={`${nm.raised} rounded-lg px-3 py-1.5 text-xs font-semibold text-[#6b7280] flex items-center gap-1.5 hover:shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] transition-all`}>
              <X className="h-3 w-3" /> Cancel
            </button>
          </div>
        </div>
      )}

      {/* Rule list */}
      {loading ? (
        <div className={`${nm.inset} rounded-xl p-4 flex items-center justify-center gap-2 text-xs text-[#6b7280]`}>
          <Loader2 className="h-3 w-3 animate-spin" /> Loading rules…
        </div>
      ) : rules.length === 0 ? (
        <div className={`${nm.inset} rounded-xl p-6 text-center`}>
          <p className="text-xs text-[#6b7280]">No rules yet. Click "New Rule" to add one.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rules.map(r => {
            const color = severityColor[r.severity] || severityColor.medium;
            const cond = typeof r.condition === "string" ? JSON.parse(r.condition) : r.condition;
            return (
              <div key={r.id} className={`${nm.card} rounded-xl px-3 py-2 ${!r.active ? "opacity-50" : ""}`}>
                <div className="flex items-start justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <div className="w-1.5 h-5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                    <div className="min-w-0">
                      <p className="text-xs font-semibold text-[#111827] truncate">{r.name}</p>
                      <p className="text-[9px] text-[#6b7280] font-mono truncate">
                        {cond.field} {cond.operator}{cond.value ? ` "${cond.value}"` : ""} → {r.action}
                        {r.domain ? ` · ${r.domain}` : ""}
                        {r.market ? ` · ${r.market}` : ""}
                        {r.reference ? ` · ${r.reference}` : ""}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => toggleActive(r)}
                      className={`${nm.raised} rounded-full px-2 py-0.5 text-[9px] font-semibold transition-all hover:shadow-[inset_2px_2px_5px_#c8c3b8,inset_-2px_-2px_5px_#ffffff]`}
                      style={{ color: r.active ? "#16a34a" : "#9ca3af" }}>
                      {r.active ? "ON" : "OFF"}
                    </button>
                    <button onClick={() => openEdit(r)}
                      className={`${nm.raised} rounded-lg p-1 text-[#6b7280] hover:shadow-[inset_2px_2px_5px_#c8c3b8,inset_-2px_-2px_5px_#ffffff] transition-all`}>
                      <Pencil className="h-3 w-3" />
                    </button>
                    <button onClick={() => handleDelete(r.id)}
                      className={`${nm.raised} rounded-lg p-1 text-[#dc2626] hover:shadow-[inset_2px_2px_5px_#c8c3b8,inset_-2px_-2px_5px_#ffffff] transition-all`}>
                      <Trash2 className="h-3 w-3" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main Panel ───────────────────────────────────────────────────────────────

interface Props { mongoProjectId: string; }

export default function CompliancePanel({ mongoProjectId }: Props) {
  const { token } = useAuth();
  const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

  const [baProjectId, setBaProjectId]     = useState<string | null>(null);
  const [requirements, setRequirements]   = useState<any[]>([]);
  const [selectedReqId, setSelectedReqId] = useState<string>("");
  const [reviewerName, setReviewerName]   = useState<string>("");
  const [running, setRunning]             = useState(false);
  const [result, setResult]               = useState<any>(null);
  const [savedAnalysis, setSavedAnalysis] = useState<any>(null);
  const [activeView, setActiveView]       = useState<"analysis" | "rules" | "manage">("analysis");
  const [error, setError]                 = useState<string | null>(null);

  useEffect(() => { init(); }, [mongoProjectId]);

  const init = async () => {
    try {
      const { data: project } = await axios.get(`${BASE}/api/tools/projects/by-mongo/${mongoProjectId}`, { headers: authHeader });
      setBaProjectId(project.id);
      const { data: full } = await axios.get(`${BASE}/api/tools/projects/${project.id}`, { headers: authHeader });
      setRequirements(full.requirements || []);
    } catch {}
  };

  const loadSaved = async (reqId: string) => {
    if (!reqId) return;
    try {
      const { data } = await axios.get(`${BASE}/api/tools/legal-analysis/${reqId}`, { headers: authHeader });
      setSavedAnalysis(data);
    } catch { setSavedAnalysis(null); }
  };

  const handleReqChange = (id: string) => { setSelectedReqId(id); setResult(null); setError(null); loadSaved(id); };

  const runLegalAnalysis = async () => {
    if (!selectedReqId || !baProjectId) return;
    setRunning(true); setResult(null); setError(null);
    const start = Date.now();
    try {
      const { data } = await axios.post(`${BASE}/api/tools/legal-analysis`,
        { requirement_id: selectedReqId, project_id: baProjectId, reviewer_name: reviewerName.trim() || null },
        { headers: authHeader }
      );
      setResult(data); setSavedAnalysis(null);
      await axios.post(`${BASE}/api/tools/log-run`, {
        project_id: baProjectId, agent_name: "legal_compliance_agent",
        input: { requirement_id: selectedReqId },
        output: { risk_score: data.analysis?.risk_score, issues: data.analysis?.issues?.length },
        model: "llama3.2+rules-engine",
        tools_called: ["evaluate_compliance_rules", "search_documents", "legal_analysis_llm"],
        duration_ms: Date.now() - start, status: "success",
      }, { headers: authHeader });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Analysis failed");
    } finally { setRunning(false); }
  };

  const runQuickCheck = async () => {
    if (!selectedReqId) return;
    const req = requirements.find(r => r.id === selectedReqId);
    if (!req) return;
    setRunning(true); setResult(null); setError(null);
    try {
      const { data } = await axios.post(`${BASE}/api/tools/evaluate-rules`, {
        feature: req.feature, channel: req.channel, contains_phi: req.contains_phi,
        market: req.market, sensitivity_flags: req.sensitivity_flags || [],
      }, { headers: authHeader });
      setResult({ rule_engine: data, analysis: null });
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Check failed");
    } finally { setRunning(false); }
  };

  const exportMemo = () => {
    const analysis = result?.analysis || (savedAnalysis ? JSON.parse(typeof savedAnalysis.legal_analysis === "string" ? savedAnalysis.legal_analysis : JSON.stringify(savedAnalysis.legal_analysis)) : null);
    const req = requirements.find(r => r.id === selectedReqId);
    if (!analysis || !req) return;
    const lines = [
      "═══════════════════════════════════════════════════════",
      "              LEGAL COMPLIANCE ANALYSIS MEMO",
      "═══════════════════════════════════════════════════════",
      "", `Requirement: ${req.title}`, `Analysed: ${new Date().toLocaleString()}`,
      ...(reviewerName ? [`Reviewed by: ${reviewerName}`] : []),
      "", `RISK SCORE: ${analysis.risk_score}/100 — ${(analysis.risk_level || "").toUpperCase()} RISK`,
      "", "EXECUTIVE SUMMARY", analysis.executive_summary || "",
      ...(analysis.issues || []).flatMap((issue: any, i: number) => [
        "─────────────────────────────────────────────────────",
        `ISSUE ${i + 1}: ${issue.issue}`, `RULE: ${issue.rule_reference}`,
        `CONCLUSION: ${issue.conclusion}`,
        ...(issue.penalty_exposure ? [`PENALTY: ${issue.penalty_exposure}`] : []),
        ...(issue.remediation?.length ? ["REMEDIATION:", ...issue.remediation.map((s: string, j: number) => `  ${j + 1}. ${s}`)] : []),
      ]),
      "═══════════════════════════════════════════════════════",
      "DISCLAIMER: Does not constitute legal advice.",
      "═══════════════════════════════════════════════════════",
    ];
    const blob = new Blob([lines.join("\n")], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    const slug = req.title.toLowerCase().replace(/\s+/g, "_").replace(/[^a-z0-9_]/g, "").slice(0, 40);
    a.download = `compliance_memo_${slug}_${new Date().toISOString().slice(0, 10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const displayAnalysis = result?.analysis || (savedAnalysis ? JSON.parse(typeof savedAnalysis.legal_analysis === "string" ? savedAnalysis.legal_analysis : JSON.stringify(savedAnalysis.legal_analysis)) : null);
  const displayRules = result?.rule_engine;
  const hasSaved = !!savedAnalysis?.legal_analysis;
  const hasResult = !!result;

  return (
    <div className={`min-h-full ${nm.base} p-4 space-y-3 max-w-2xl`}>

      {/* ── HEADER ── */}
      <div className={`${nm.raised} rounded-xl px-3 py-2 flex items-center gap-2`}>
        <div className="bg-[#2d5bbf] rounded-lg p-1.5">
          <Scale className="h-3.5 w-3.5 text-white" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-[#111827]">Legal Compliance</h2>
          <p className="text-[10px] text-[#6b7280]">IRAC reasoning · exact citations · penalty exposure · remediation</p>
        </div>
      </div>

      {/* ── HOW IT WORKS ── */}
      <div className={`${nm.inset} rounded-xl px-3 py-2 text-[10px] text-[#1f2937] space-y-0.5`}>
        <p className="font-semibold text-[#111827] flex items-center gap-1"><span className="text-[#6b7280]">ℹ</span> Two-layer analysis</p>
        <p><span className="font-semibold">Layer 1 — Rule Engine:</span> Deterministic checks. Fast, no AI.</p>
        <p><span className="font-semibold">Layer 2 — AI Legal Analysis:</span> Llama applies IRAC, cites exact regulation text, produces remediation playbook.</p>
      </div>

      {/* ── CONTROLS ── */}
      <div className={`${nm.raised} rounded-xl p-3 space-y-2`}>
        <select
          value={selectedReqId}
          onChange={e => handleReqChange(e.target.value)}
          className={`w-full ${nm.inset} rounded-lg text-xs px-2 py-1.5 text-[#111827] focus:outline-none border-none`}
          style={{ background: "#e4ecfb" }}
        >
          <option value="">— Select a requirement —</option>
          {requirements.map(r => (
            <option key={r.id} value={r.id}>
              {r.title}{r.contains_phi ? " [PHI]" : ""}{r.market ? ` · ${r.market}` : ""}
            </option>
          ))}
        </select>

        <Input
          placeholder="Reviewer name (optional)"
          value={reviewerName}
          onChange={e => setReviewerName(e.target.value)}
          className="border-none shadow-none h-8 text-xs text-[#111827] bg-[#e4ecfb] focus-visible:ring-0"
          style={{ boxShadow: "inset 4px 4px 8px #bcc8e0, inset -4px -4px 8px #ffffff" }}
        />

        <div className="flex gap-2">
          <button
            onClick={runLegalAnalysis}
            disabled={!selectedReqId || running}
            className={`flex-1 ${nm.raised} rounded-lg py-1.5 text-xs font-semibold text-[#111827] flex items-center justify-center gap-1.5 hover:shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] transition-all disabled:opacity-40`}
          >
            {running ? <Loader2 className="h-3 w-3 animate-spin" /> : <Scale className="h-3 w-3" />}
            Full Legal Analysis
          </button>
          <button
            onClick={runQuickCheck}
            disabled={!selectedReqId || running}
            className={`${nm.raised} rounded-lg px-3 py-1.5 text-xs font-semibold text-[#111827] flex items-center gap-1.5 hover:shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] transition-all disabled:opacity-40`}
          >
            <ShieldAlert className="h-3 w-3" /> Quick Check
          </button>
        </div>
      </div>

      {/* ── LOADING ── */}
      {running && (
        <div className={`${nm.inset} rounded-xl px-3 py-2 flex items-center gap-2 text-xs text-[#374151]`}>
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Running rule engine + AI legal analysis… (30–60 seconds)
        </div>
      )}

      {/* ── ERROR ── */}
      {error && (
        <div className="rounded-xl px-3 py-2 bg-[#fee2e2] text-xs text-[#991b1b]">{error}</div>
      )}

      {/* ── VIEW TOGGLE ── */}
      <div className={`${nm.raised} rounded-xl p-1 flex gap-1`}>
        {(hasResult || hasSaved) && displayAnalysis && (
          <button
            onClick={() => setActiveView("analysis")}
            className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-all ${activeView === "analysis" ? "shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] text-[#111827]" : "text-[#6b7280]"}`}
          >
            Legal Analysis{hasSaved && !hasResult ? " (Saved)" : ""}
          </button>
        )}
        {(hasResult || hasSaved) && (
          <button
            onClick={() => setActiveView("rules")}
            className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-all ${activeView === "rules" ? "shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] text-[#111827]" : "text-[#6b7280]"}`}
          >
            Rule Engine
          </button>
        )}
        <button
          onClick={() => setActiveView("manage")}
          className={`flex-1 rounded-lg py-1.5 text-xs font-semibold transition-all ${activeView === "manage" ? "shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] text-[#111827]" : "text-[#6b7280]"}`}
        >
          Manage Rules
        </button>
      </div>

      {/* ── LEGAL ANALYSIS VIEW ── */}
      {activeView === "analysis" && displayAnalysis && !running && (
        <div className="space-y-3">
          {hasSaved && !hasResult && (
            <p className="text-[10px] text-[#6b7280] px-1">
              Saved · {savedAnalysis?.created_at ? new Date(savedAnalysis.created_at).toLocaleString() : "previous run"}
              {savedAnalysis?.reviewed_by && ` · ${savedAnalysis.reviewed_by}`}
            </p>
          )}

          <RiskMeter score={displayAnalysis.risk_score || 0} level={displayAnalysis.risk_level || "clear"} />

          {displayAnalysis.applicable_frameworks?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {displayAnalysis.applicable_frameworks.map((f: string) => (
                <span key={f} className={`${nm.raised} rounded-full text-[9px] px-2 py-0.5 text-[#1f2937]`}>{f}</span>
              ))}
            </div>
          )}

          <div className={`${nm.inset} rounded-xl p-2`}>
            <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1">Executive Summary</p>
            <p className="text-[10px] text-[#1f2937] leading-relaxed">{displayAnalysis.executive_summary}</p>
          </div>

          {displayAnalysis.issues?.length > 0 && (
            <div className="space-y-2">
              <p className="text-[10px] font-semibold text-[#374151] uppercase tracking-wider px-1">
                {displayAnalysis.issues.length} Issue{displayAnalysis.issues.length !== 1 ? "s" : ""} Identified
              </p>
              {displayAnalysis.issues.map((issue: any, i: number) => (
                <IssueCard key={i} issue={issue} idx={i} />
              ))}
            </div>
          )}

          {displayAnalysis.safe_harbours?.length > 0 && (
            <div className={`${nm.inset} rounded-xl p-2`}>
              <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1.5 flex items-center gap-1">
                <CircleCheck className="h-2.5 w-2.5 text-[#16a34a]" /> Safe Harbours
              </p>
              {displayAnalysis.safe_harbours.map((s: string, i: number) => (
                <p key={i} className="text-[10px] text-[#1f2937] flex gap-1.5 mt-1">
                  <span className="text-[#16a34a] shrink-0">▸</span>{s}
                </p>
              ))}
            </div>
          )}

          {displayAnalysis.overall_recommendation && (
            <div className={`${nm.inset} rounded-xl p-2`}>
              <p className="text-[9px] font-semibold text-[#374151] uppercase tracking-wider mb-1 flex items-center gap-1">
                <Gavel className="h-2.5 w-2.5" /> Recommendation
              </p>
              <p className="text-[10px] text-[#1f2937] leading-relaxed">{displayAnalysis.overall_recommendation}</p>
            </div>
          )}

          <div className={`${nm.raised} rounded-xl p-2 flex items-center justify-between`}>
            <p className="text-[9px] text-[#6b7280] italic">Does not constitute legal advice. Requires legal counsel review.</p>
            <button
              onClick={exportMemo}
              className={`${nm.raised} rounded-lg px-2 py-1 text-[10px] font-semibold text-[#1f2937] flex items-center gap-1 hover:shadow-[inset_4px_4px_8px_#c8c3b8,inset_-4px_-4px_8px_#ffffff] transition-all ml-2 shrink-0`}
            >
              <Download className="h-3 w-3" /> Export .txt
            </button>
          </div>
        </div>
      )}

      {/* ── RULE ENGINE VIEW ── */}
      {activeView === "rules" && !running && (
        <div className="space-y-2">
          {displayRules ? (
            <>
              <div className={`${nm.card} rounded-xl px-3 py-2 flex items-center gap-2`}>
                {displayRules.passed
                  ? <CheckCircle2 className="h-4 w-4 text-[#16a34a] shrink-0" />
                  : <ShieldAlert className="h-4 w-4 text-[#dc2626] shrink-0" />
                }
                <div>
                  <p className="text-xs font-semibold text-[#111827]">
                    {displayRules.passed ? "No rule violations" : `${displayRules.violations.length} violation${displayRules.violations.length !== 1 ? "s" : ""} detected`}
                  </p>
                  <p className="text-[10px] text-[#6b7280]">
                    {displayRules.evaluated_rules} rules evaluated (no AI)
                    {displayRules.warnings?.length > 0 && ` · ${displayRules.warnings.length} warning${displayRules.warnings.length !== 1 ? "s" : ""}`}
                  </p>
                </div>
              </div>

              {[...(displayRules.violations || []).map((v: any) => ({ ...v, _type: "violation" })),
                 ...(displayRules.warnings  || []).map((w: any) => ({ ...w, _type: "warning"   }))
               ].map((item: any, i: number) => {
                const color = { critical: "#e07070", high: "#e09060", medium: "#c8b060", low: "#6090c8" }[item.severity as string] || "#c8b060";
                return (
                  <div key={i} className={`${nm.card} rounded-xl px-3 py-2`}>
                    <div className="flex items-center gap-2 mb-1">
                      <div className="w-1.5 h-4 rounded-full shrink-0" style={{ backgroundColor: color }} />
                      <span className="text-xs font-semibold text-[#111827]">{item.rule_name}</span>
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full ml-auto" style={{ color, backgroundColor: `${color}18` }}>
                        {item.action}
                      </span>
                    </div>
                    <p className="text-[10px] text-[#1f2937] pl-3.5">{item.message}</p>
                    {item.reference && <p className="text-[9px] text-[#6b7280] font-mono pl-3.5 mt-0.5">{item.reference}</p>}
                  </div>
                );
              })}
            </>
          ) : (
            <div className={`${nm.inset} rounded-xl p-6 text-center`}>
              <p className="text-xs text-[#6b7280]">Run a check above to see rule engine results.</p>
            </div>
          )}
        </div>
      )}

      {/* ── MANAGE RULES VIEW ── */}
      {activeView === "manage" && !running && (
        <RuleManager authHeader={authHeader} />
      )}

      {/* ── DEMO SAMPLE ── */}
      {!hasResult && !hasSaved && !running && activeView !== "manage" && <DemoSample />}

      {/* ── EMPTY STATE ── */}
      {requirements.length === 0 && !running && (
        <div className={`${nm.inset} rounded-xl p-6 text-center`}>
          <p className="text-xs text-[#6b7280]">No requirements yet — create one in the Requirements tab first.</p>
        </div>
      )}
    </div>
  );
}
